import { supabase, supabaseAnon } from '../lib/supabase'
import {
  applyFilters,
  applyOrdering,
  applyRange,
  applyRoleConstraints,
  applySearch,
  applyOwnershipToPayload,
  handleQuery,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent
} from './utils'
import * as eventsService from './eventsService'

const TABLE_NAME = 'agent_sharing_links'

const normalizeSharingLinkPayload = (payload = {}) => {
  if (!payload) return payload
  const { destinationUrl, eventId, agentId, studentId, linkUrl, link_url, destination_url, ...rest } = payload

  const rawSlug = rest.slug ?? rest.identifier
  return {
    ...rest,
    event_id: rest.event_id ?? eventId,
    agent_id: rest.agent_id ?? agentId,
    student_id: rest.student_id ?? studentId,
    slug: rawSlug != null && String(rawSlug).trim() !== '' ? String(rawSlug).trim().toLowerCase() : rawSlug
  }
}

/**
 * Fetch sharing links with optional filtering and role constraints.
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   eventIds?: string[],
 *   filters?: Array<{ column: string, operator: string, value: any }>,
 *   search?: string,
 *   searchColumns?: string[],
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number }
 * }} [options]
 */
export const listSharingLinks = async (options = {}) => {
  const {
    role,
    userId,
    eventIds,
    filters,
    search,
    searchColumns = ['slug', 'destination_url'],
    orderBy = { column: 'created_at', ascending: false },
    range
  } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && (!eventIds || eventIds.length === 0)) {
    return accessDeniedResponse('Partners can only access invitation links for assigned events.')
  }

  let query = supabase.from(TABLE_NAME).select('*')

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'student_id',
    eventIds
  })

  query = applyFilters(query, filters)
  query = applySearch(query, search, searchColumns)
  query = applyOrdering(query, orderBy)
  query = applyRange(query, range)

  return handleQuery(query, { fallbackMessage: 'Unable to load invitation links.' })
}

/**
 * Fetch a single sharing link by id.
 * @param {string} linkId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventIds?: string[] }} [options]
 */
export const getSharingLinkById = async (linkId, options = {}) => {
  const { role, userId, eventIds } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && (!eventIds || eventIds.length === 0)) {
    return accessDeniedResponse('Partners can only access invitation links for assigned events.')
  }

  let query = supabase.from(TABLE_NAME).select('*').eq('id', linkId).maybeSingle()

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'student_id',
    eventIds
  })

  return handleQuery(query, {
    fallbackMessage: 'Unable to load the invitation link.',
    allowEmpty: false
  })
}

/**
 * Create a sharing link.
 * @param {Record<string, any>} payload
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const createSharingLink = async (payload, options = {}) => {
  const { role, userId } = options
  // Resolve userId from auth when missing so agent/partner can create links even if UI state is stale
  let resolveOptions = { role, userId }
  if ((role === 'agent' || role === 'partner') && !userId) {
    const { data: authUser } = await supabase.auth.getUser()
    if (authUser?.user?.id) resolveOptions = { ...resolveOptions, userId: authUser.user.id }
  }
  const roleContext = await resolveRoleContext(resolveOptions)
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && !payload?.event_id) {
    return accessDeniedResponse('Partners can only create invitation links for assigned events.')
  }

  // For agent/partner creating a sharing link: allow only when the event exists and is public.
  // Do not use ensureCanManageEvent here — any authenticated agent/partner may create a link for public events.
  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && payload?.event_id) {
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('id, status')
      .eq('id', payload.event_id)
      .maybeSingle()
    if (eventError) {
      return { data: null, error: eventError }
    }
    if (!event) {
      return accessDeniedResponse('Event not found.')
    }
    if (event.status !== 'public') {
      return accessDeniedResponse('Only public events can have invitation links created. Publish the event first.')
    }
  }

  const normalizedPayload = normalizeSharingLinkPayload(payload)

  // For agent/partner, always use current auth.uid() for agent_id so RLS INSERT (agent_id = auth.uid()) passes.
  let effectiveUserId = resolvedUserId
  let authUid = null
  if (resolvedRole === 'agent' || resolvedRole === 'partner') {
    const { data: authUser } = await supabase.auth.getUser()
    authUid = authUser?.user?.id ?? null
    if (!authUid) {
      return { data: null, error: accessDeniedResponse('You must be signed in to create an invitation link.').error }
    }
    effectiveUserId = effectiveUserId ?? authUid
  }

  const data = applyOwnershipToPayload(normalizedPayload, {
    role: resolvedRole,
    userId: effectiveUserId,
    agentField: 'agent_id',
    studentField: 'student_id'
  })

  if (resolvedRole === 'agent' || resolvedRole === 'partner') {
    data.agent_id = authUid
  } else if (resolvedRole === 'admin') {
    if (!normalizedPayload.agent_id) {
      return accessDeniedResponse('An agent must be selected when creating an invitation link as admin.')
    }
    data.agent_id = normalizedPayload.agent_id
  }
  const result = await handleQuery(
    supabase.from(TABLE_NAME).insert(data).select('*').single(),
    {
      fallbackMessage: 'Unable to create the invitation link.',
      allowEmpty: false
    }
  )

  if (result.data) {
    await supabase.rpc('notify_sharing_link_created', {
      p_agent_id: result.data.agent_id,
      p_event_id: result.data.event_id,
      p_slug: result.data.slug ?? ''
    }).then(() => {}).catch(() => {})
  }

  return result
}

/**
 * Update a sharing link.
 * @param {string} linkId
 * @param {Record<string, any>} updates
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const updateSharingLink = async (linkId, updates, options = {}) => {
  const { role, userId, eventId, eventIds } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner') {
    const scopedEventId = eventId || updates?.event_id
    if (!scopedEventId && (!eventIds || eventIds.length === 0)) {
      return accessDeniedResponse('Partners can only update invitation links for assigned events.')
    }
  }

  const scopedEventId = eventId || updates?.event_id
  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && scopedEventId) {
    const access = await ensureCanManageEvent(scopedEventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  const normalizedUpdates = normalizeSharingLinkPayload(updates)
  let query = supabase.from(TABLE_NAME).update(normalizedUpdates).eq('id', linkId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'student_id',
    eventIds
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to update the invitation link.',
    allowEmpty: false
  })
}

/**
 * Fetch sharing link stats by link id.
 * @param {string} linkId
 */
export const getShareLinkStats = async (linkId) => {
  const query = supabase.rpc('get_sharing_link_stats', { p_link_id: linkId })

  return handleQuery(query, {
    fallbackMessage: 'Unable to load invitation link stats.',
    allowEmpty: false
  })
}

/**
 * Fetch agent sharing analytics by agent id.
 * @param {string} agentId
 */
export const getAgentAnalytics = async (agentId) => {
  const query = supabase.rpc('get_agent_sharing_analytics', { p_agent_id: agentId })

  return handleQuery(query, {
    fallbackMessage: 'Unable to load agent invitation link analytics.',
    allowEmpty: false
  })
}

/**
 * Delete a sharing link.
 * @param {string} linkId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const deleteSharingLink = async (linkId, options = {}) => {
  const { role, userId, eventId, eventIds } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner') {
    if (!eventId && (!eventIds || eventIds.length === 0)) {
      return accessDeniedResponse('Partners can only delete invitation links for assigned events.')
    }
  }

  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && eventId) {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  let query = supabase.from(TABLE_NAME).delete().eq('id', linkId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'agent_id',
    studentField: 'student_id',
    eventIds
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to delete the invitation link.',
    allowEmpty: false
  })
}

/**
 * Resolve a short link by event slug and agent slug.
 * @param {string} eventSlug
 * @param {string} agentSlug
 * @returns {Promise<{ data: { event_id: string, agent_id: string, link_id: string, slug: string } | null, error: any }>}
 */
export const resolveShortLink = async (eventSlug, agentSlug) => {
  try {
    console.log('=== resolveShortLink Debug ===');
    console.log('Input - eventSlug:', eventSlug);
    console.log('Input - agentSlug:', agentSlug);

    // Find public event by slug (service uses status = 'public', no agent_id filter; RLS governs visibility)
    console.log('Step 1: Looking up public event by slug:', eventSlug);
    const { data: event, error: eventError } = await eventsService.getPublicEventBySlug(eventSlug);

    console.log('Event query result - data:', event);
    console.log('Event query result - error:', eventError);

    if (eventError || !event) {
      const err = eventError || { message: 'Event not found' };
      console.error('Event lookup failed:', err);
      return { data: null, error: err }
    }

    console.log('Event found successfully:', { id: event.id, slug: event.event_slug, title: event.title });

    // Find sharing link by agent slug and event_id (case-insensitive: compare as lowercase)
    const normAgentSlug = (agentSlug && String(agentSlug).trim().toLowerCase()) || agentSlug
    console.log('Step 2: Looking up sharing link');
    console.log('Query parameters:', { slug: normAgentSlug, event_id: event.id });

    // Use anon client to avoid auth refresh blocking public requests (CORS/522 cascade)
    const { data: sharingLink, error: linkError } = await supabaseAnon
      .from(TABLE_NAME)
      .select('*')
      .eq('slug', normAgentSlug)
      .eq('event_id', event.id)
      .maybeSingle()

    console.log('Sharing link query result - data:', sharingLink);
    console.log('Sharing link query result - error:', linkError);

    if (linkError) {
      console.error('Sharing link lookup failed with error:', linkError);
      console.error('Link error details:', {
        message: linkError.message,
        code: linkError.code,
        details: linkError.details,
        hint: linkError.hint
      });
      return { data: null, error: linkError }
    }

    if (!sharingLink) {
      console.error('Sharing link not found');
      console.error('Search criteria:', { slug: agentSlug, event_id: event.id, table: TABLE_NAME });
      return { data: null, error: { message: 'Invitation link not found' } }
    }

    console.log('Sharing link found successfully:', {
      id: sharingLink.id,
      slug: sharingLink.slug,
      event_id: sharingLink.event_id,
      agent_id: sharingLink.agent_id
    });

    const result = {
      data: {
        event_id: sharingLink.event_id,
        agent_id: sharingLink.agent_id,
        link_id: sharingLink.id,
        slug: sharingLink.slug
      },
      error: null
    };

    console.log('Final result:', result);
    return result;
  } catch (error) {
    console.error('Uncaught error in resolveShortLink:', error);
    console.error('Error stack:', error.stack);
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    return { data: null, error }
  }
}

/**
 * Fetch a sharing link by slug (optionally scoped to an event).
 * @param {string} slug
 * @param {{ eventId?: string }} [options]
 */
/**
 * Record a click on a public sharing link (anon-safe RPC).
 * @param {string} sharingLinkCode - Normalized agent slug
 * @param {{ ipAddress?: string, userAgent?: string }} [options]
 */
export const recordLinkClick = async (sharingLinkCode, options = {}) => {
  const { ipAddress = 'unknown', userAgent = '' } = options

  if (!sharingLinkCode) {
    return { data: null, error: { message: 'Sharing link code is required.' } }
  }

  return handleQuery(
    supabaseAnon.rpc('record_link_click', {
      p_sharing_link_code: sharingLinkCode,
      p_ip_address: ipAddress,
      p_user_agent: userAgent
    }),
    {
      fallbackMessage: 'Unable to record link click.',
      allowEmpty: true
    }
  )
}

export const getSharingLinkBySlug = async (slug, options = {}) => {
  if (!slug) {
    return { data: null, error: null }
  }

  const { eventId } = options
  const normSlug = String(slug).trim().toLowerCase()

  let query = supabase
    .from(TABLE_NAME)
    .select('id, slug, event_id')
    .eq('slug', normSlug)
    .maybeSingle()

  if (eventId) {
    query = query.eq('event_id', eventId)
  }

  return handleQuery(query, {
    fallbackMessage: 'Unable to load the invitation link.'
  })
}
